import { forwardRef, useCallback, useMemo } from 'react'
import ReactSelect, { components } from 'react-select'
import type {
  PropsValue,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  OptionProps,
  ValueContainerProps,
} from 'react-select'
import classnames from 'classnames'

import {
  AlertCircleIcon,
  DropdownDownIcon,
  CircleXFilledIcon,
  CheckIcon,
} from '../../assets/icons/icons'

import styles from './index.module.scss'

export type SelectValue = {
  label: string | JSX.Element
  value: string
  description?: string
}

export type SelectOptionValueProps = SelectValue & {
  isDisabled?: boolean
  isFixed?: boolean
}

export type SelectOptionSectionProps = {
  label: string
  options: SelectOptionValueProps[]
}

export type SelectOptionOrSectionProps = SelectOptionValueProps | SelectOptionSectionProps

export type SelectProps = {
  value?: PropsValue<SelectValue>
  onChange: (newValue: PropsValue<SelectOptionValueProps>) => void
  startIcon?: React.ElementType | Node | string | JSX.Element
  className?: string
  id: string
  label: string
  labelClassName?: string
  options: SelectOptionOrSectionProps[]
  placeholder?: string
  size?: 'small' | 'medium'
  helperText?: string | React.ReactNode
  error?: string | React.ReactNode
  isOptional?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  hiddenLabel?: boolean
  variant?: 'default' | 'ghost'
  type?: 'default' | 'button'
  onInputChange?: (newValue: string) => void
}

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      value,
      onChange,
      startIcon,
      className = '',
      id,
      isOptional = false,
      label,
      labelClassName = '',
      placeholder,
      options,
      size = 'small',
      helperText = '',
      error = '',
      isDisabled,
      isLoading,
      isClearable,
      isSearchable = false,
      hiddenLabel,
      variant = 'default',
      type = 'default',
      onInputChange,
      ...rest
    }: SelectProps,
    ref
  ) => {
    const selectId = `select-${id}`

    const DropdownIndicator = useCallback(
      (props: DropdownIndicatorProps<SelectOptionValueProps>) => {
        return (
          <components.DropdownIndicator {...props}>
            <DropdownDownIcon className={styles['indicator-icon']} />
          </components.DropdownIndicator>
        )
      },
      []
    )

    const ClearIndicator = useCallback((props: ClearIndicatorProps<SelectOptionValueProps>) => {
      return (
        <components.ClearIndicator {...props}>
          <CircleXFilledIcon className={styles['clear-icon']} />
        </components.ClearIndicator>
      )
    }, [])

    const ValueContainer = useCallback(
      ({ children, ...props }: ValueContainerProps<SelectOptionValueProps>) => {
        const StartIcon = startIcon as React.ElementType
        return (
          <components.ValueContainer
            {...props}
            className={classnames({
              [styles['select-button']]: type === 'button',
            })}
          >
            {startIcon && <StartIcon className={styles['start-icon']} />}
            {children}
          </components.ValueContainer>
        )
      },
      [startIcon, type]
    )

    const Option = useCallback((props: OptionProps<SelectOptionValueProps>) => {
      return (
        <components.Option {...props}>
          <div>
            <p className={styles['option-label']}>{props.data.label}</p>
            {props.data.description && (
              <p className={styles['option-description']}>{props.data.description}</p>
            )}
          </div>
          <CheckIcon className={styles['option-end-icon']} />
        </components.Option>
      )
    }, [])

    const renderHelperText = useMemo(() => {
      if (!helperText && !error) {
        return null
      }

      return (
        <div className={styles['helper-text-wrapper']}>
          {!!error && <AlertCircleIcon className={styles['helper-icon']} />}
          <p
            className={classnames(styles['helper-text'], {
              [styles['helper-text-error']]: !!error,
            })}
          >
            {error || helperText}
          </p>
        </div>
      )
    }, [error, helperText])

    const NoFilterOption = useCallback(() => true, [])

    return (
      <div
        ref={ref}
        className={classnames(styles['wrapper'], styles[size], className, {
          [styles['error']]: !!error,
          [styles['disabled']]: isDisabled,
          [styles['with-start-icon']]: !!startIcon,
          [styles['select-button']]: type === 'button',
        })}
      >
        <label
          htmlFor={selectId}
          className={classnames(styles['label'], labelClassName, {
            [styles['visually-hidden']]: hiddenLabel,
          })}
        >
          {label}
        </label>
        {isOptional && <p className={styles['optional']}>Optional</p>}
        <ReactSelect
          {...rest}
          inputId={selectId}
          instanceId={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          noOptionsMessage={() => 'No results'}
          className={classnames(styles['select-wrapper'], styles[variant], {
            [styles['select-button']]: type === 'button',
            [styles['applied']]: value !== null,
          })}
          classNamePrefix="select"
          components={{ ClearIndicator, DropdownIndicator, ValueContainer, Option }}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          onInputChange={onInputChange}
          options={options}
          filterOption={onInputChange ? NoFilterOption : undefined}
        />
        {renderHelperText}
      </div>
    )
  }
)

export default Select
